import MDEditor from '@uiw/react-md-editor';
import axios from 'axios';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FaCopy, FaGlobe, FaLink, FaUpload } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Adminnav from './AdminNav';

const PostProducts = () => {
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [image, setImage] = useState(null);
  const [country, setCountry] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setError('Please select an image.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB.');
      return;
    }
    setImage(file);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post('/api/product/postPic', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setImageUrl(response.data.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !image || !shortDescription || !content || !country) {
      setError('Please fill all the required fields');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('shortDescription', shortDescription);
    formData.append('image', image);
    formData.append('content', content);
    formData.append('country', country);

    try {
      await axios.post('/api/product', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setError('');
      setTitle('');
      setShortDescription('');
      setImage(null);
      setCountry('');
      setContent('');
      toast.success('Product created successfully');
      navigate('/admin');
    } catch (error) {
      console.error('Error submitting post:', error);
      setError('Failed to submit the product. Please try again.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(imageUrl);
    toast.success('Image URL copied to clipboard!');
  };

  return (
    <div className="min-h-screen  w-full bg-gradient-to-br from-gray-50 to-gray-100 font-roboto flex flex-col">
      <Adminnav />

      {/* Main Content */}
      <div className="flex-1 flex md:ml-[255px] flex-col">
        {/* Title Section */}
        <motion.div
          className="w-full bg-gray-900 p-3 pl-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h1 className="text-2xl  pl-5  sm:text-3xl py-2 font-semibold text-white ">
            Create New Product
          </h1>
        </motion.div>

        {/* Product Form Container */}
        <motion.div
          className="w-full bg-white shadow-xl overflow-hidden "
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col lg:flex-row">
            {/* Product Info Section */}
            <motion.div
              className="w-full lg:w-2/5 bg-gray-800 p-8 sm:p-10 lg:p-12 text-white"
              initial={{ x: -20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                Product Details
              </h2>
              <p className="text-indigo-100 mb-8 text-lg">
                Provide basic information about your product
              </p>

              {error && (
                <motion.div
                  className="bg-red-50 border-l-4 border-red-500 p-4 mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-red-700">{error}</p>
                </motion.div>
              )}

              <div className="space-y-6">
                {/* Product Name */}
                <motion.div
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <label className="block text-sm font-medium text-indigo-100 mb-2">
                    Product Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter product name"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-300 transition"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </motion.div>

                {/* Product Country */}
                <motion.div
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <label className="block text-sm font-medium text-indigo-100 mb-2">
                    Country of Origin
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaGlobe className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Enter country"
                      className="w-full pl-10 px-4 py-3 bg-gray-700 border border-gray-600  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-300 transition"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                    />
                  </div>
                </motion.div>

                {/* Short Description */}
                <motion.div
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <label className="block text-sm font-medium text-indigo-100 mb-2">
                    Short Description
                  </label>
                  <textarea
                    placeholder="Brief description of the product"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600  focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white placeholder-gray-300 transition h-32"
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    required
                  />
                </motion.div>

                {/* Image Upload */}
                <motion.div
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <label className="block text-sm font-medium text-indigo-100 mb-2">
                    Product Image
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      className="w-full opacity-0 absolute inset-0 cursor-pointer"
                      onChange={handleFileChange}
                      required
                    />
                    <div className="w-full px-4 py-3 bg-gray-700 border border-gray-600  flex items-center justify-between cursor-pointer hover:bg-gray-600 transition">
                      <span className="text-gray-300">
                        {image ? image.name : 'Select an image'}
                      </span>
                      <FaUpload className="text-indigo-300" />
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                className="mt-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <Link
                  to="/post/service"
                  className="text-indigo-300 hover:text-white transition flex items-center"
                >
                  <span>Need to post a service instead?</span>
                </Link>
              </motion.div>
            </motion.div>

            {/* Content Editor Section */}
            <motion.div
              className="w-full lg:w-3/5 p-8 sm:p-10 lg:p-12"
              initial={{ x: 20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
                Product Content
              </h2>

              <div className="space-y-6">
                {/* Markdown Editor */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  data-color-mode="light"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description
                  </label>
                  <MDEditor
                    value={content}
                    onChange={setContent}
                    height={400}
                    visibleDragbar={false}
                    previewOptions={{ className: 'md-editor-light' }}
                  />
                </motion.div>

                {/* Image URL Upload */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Get Image URL
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="pic"
                      className="w-full opacity-0 absolute inset-0 cursor-pointer"
                      onChange={handleImageUpload}
                    />
                    <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300  flex items-center justify-between cursor-pointer hover:bg-gray-200 transition">
                      <span className="text-gray-700">
                        {loading ? 'Uploading...' : 'Upload Image for URL'}
                      </span>
                      <FaLink className="text-indigo-500" />
                    </div>
                  </div>
                </motion.div>

                {imageUrl && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="space-y-2"
                  >
                    <label className="block text-sm font-medium text-gray-700">
                      Image URL
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={imageUrl}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300  bg-gray-50 text-gray-700"
                      />
                      <button
                        onClick={copyToClipboard}
                        className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 transition flex items-center space-x-2"
                      >
                        <FaCopy />
                        <span>Copy</span>
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="pt-4"
                >
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6  transition flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Creating Product...
                      </span>
                    ) : (
                      'Create Product'
                    )}
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PostProducts;
